import React, { useEffect, useMemo, useRef, useState } from 'react';
import comms from '../../comms';
import { normalizeMapLatLng } from './locationHelper';
import { useFlagTripQuote } from '../FlagTrip/useFlagTripQuote';
import { computeMileageAndDuration, friendlyServerError } from '../FlagTrip/computeTravel';
import FlagTripPrepayPanel from '../FlagTrip/FlagTripPrepayPanel';
import { reverseGeocodeCoords } from '../FlagTrip/addressValidation';
import LocationSearchBox from '../FlagTrip/LocationSearchBox';
import { getRecentDestinations, recordDestinationUse } from '../FlagTrip/recentDestinations';

/**
 * Backstop for "dispatch never replied". JOBMF arrival is the canonical ack — this only
 * fires when the link is stuck or the request hit a server error that no MSG/JOBMF reports.
 */
const DISPATCH_BACKSTOP_MS = 12000;

/**
 * Dispatch sends this when FLAGM coordinates cannot be reverse-geocoded / zoned.
 * Match loosely so minor server wording changes still surface in the modal.
 */
function isFlagTripAddressFailureMessage(text) {
  if (!text || typeof text !== 'string') return false;
  const t = text.toLowerCase();
  return t.includes('derive address') && t.includes('flag trip');
}

/**
 * LiveDispatch modal: flag an unscheduled street pickup (FLAGM socket message).
 *
 * Rate selection, an optional/required destination, and a server-priced quote (with
 * a signed quoteToken) now precede submission — see docs/DriverRequirement/
 * 03_create_and_flag_trip.md and 03_flag_trip_client_handoff.md. Rate/quote/pre-trip-
 * payment state is shared with Itinerary's FlagTripCreateModal via useFlagTripQuote;
 * only the final creation call differs (extended FLAGM here vs createItineraryFlagTrip
 * there).
 *
 * Resolution after Submit:
 *  - The parent registers `registerPendingFlagAck(resolveFn)` so when JOBMF arrives in
 *    LiveDispatch.onFlagTrip, it resolves the modal with the new jobNumber.
 *  - Address-failure MSG (no JOBMF) flips the modal back to error state.
 *  - DISPATCH_BACKSTOP_MS timeout shows "Dispatch did not respond" rather than auto-success.
 *  - TRIPERR (rate/quote/payment lifecycle errors) surfaces via comms.setFlagTripErrorHandler.
 */
const FlagTripModal = ({ isOpen, onClose, currentLocation, onSuccess, registerPendingFlagAck }) => {
  const [passengerCount, setPassengerCount] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [awaitingDispatch, setAwaitingDispatch] = useState(false);
  const [error, setError] = useState(null);

  const [destination, setDestination] = useState(null); // {address,lat,lon,streetNumber,...} | null
  const [wantsDestination, setWantsDestination] = useState(false); // explicit "Add destination" toggle
  // Pickup address resolved from the driver's GPS. Sent alongside the origin coords so
  // dispatch doesn't have to reverse-geocode them itself — that server-side lookup can
  // fail or return a placeholder (we've seen "0 <street>" with an "Unknown" city), which
  // then shows up as the trip's pickup for the rest of its life.
  const [pickup, setPickup] = useState(null);
  const [isResolvingPickup, setIsResolvingPickup] = useState(false);
  const [travel, setTravel] = useState({ mileage: 0, durationSeconds: 0 });
  const [quotedOrigin, setQuotedOrigin] = useState(null); // origin snapshot the current quote was priced from

  const lastSubmitRef = useRef(0);
  const awaitingDispatchRef = useRef(false);
  const dispatchWaitTimerRef = useRef(null);
  const SUBMIT_COOLDOWN_MS = 5000;

  const userSettings = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('userSettings')) || {}; } catch { return {}; }
  }, []);
  const vehicleId = userSettings.VehicleID || userSettings.vehicleId;
  const driverId = userSettings.Id;

  const origin = normalizeMapLatLng(currentLocation);

  const {
    ratesError, selectedRateId, selectedRate,
    quote, isQuoting, quoteError, requestQuote, invalidateQuote: invalidateQuoteBase,
    prepay, requestPrepay, checkPrepayStatus, isCheckingPrepay,
  } = useFlagTripQuote({
    isOpen,
    mode: 'live',
    vehicleId,
    originLat: origin?.lat,
    originLon: origin?.lng,
  });

  // Also drop the frozen quote-time origin snapshot whenever the quote itself is invalidated —
  // otherwise a stale snapshot could linger and get reused for an unrelated later quote.
  const invalidateQuote = () => {
    invalidateQuoteBase();
    setQuotedOrigin(null);
  };

  const clearDispatchWait = () => {
    awaitingDispatchRef.current = false;
    setAwaitingDispatch(false);
    comms.setFlagTripAwaitingMessageHandler(null);
    comms.setFlagTripErrorHandler(null);
    if (typeof registerPendingFlagAck === 'function') {
      try { registerPendingFlagAck(null); } catch (_) { /* ignore */ }
    }
    if (dispatchWaitTimerRef.current) {
      clearTimeout(dispatchWaitTimerRef.current);
      dispatchWaitTimerRef.current = null;
    }
  };

  const finishAwaitSuccess = (jobNumber) => {
    clearDispatchWait();
    if (onSuccess) onSuccess(jobNumber);
    if (onClose) onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setPassengerCount(1);
      setDestination(null);
      setWantsDestination(false);
      setPickup(null);
      setTravel({ mileage: 0, durationSeconds: 0 });
      setQuotedOrigin(null);
      setError(null);
      setIsSubmitting(false);
      setAwaitingDispatch(false);
      awaitingDispatchRef.current = false;
      comms.setFlagTripAwaitingMessageHandler(null);
      comms.setFlagTripErrorHandler(null);
      if (dispatchWaitTimerRef.current) {
        clearTimeout(dispatchWaitTimerRef.current);
        dispatchWaitTimerRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Resolve the pickup address from the current GPS fix once, when the modal opens.
  // Deliberately keyed on isOpen (not on `origin`) so a drifting GPS fix doesn't
  // re-trigger geocoding on every position update while the driver is filling the form.
  useEffect(() => {
    if (!isOpen) return undefined;
    const lat = origin?.lat;
    const lng = origin?.lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;

    let cancelled = false;
    setIsResolvingPickup(true);
    reverseGeocodeCoords(lat, lng)
      .then((resolved) => {
        if (cancelled) return;
        setPickup(resolved);
      })
      .finally(() => {
        if (!cancelled) setIsResolvingPickup(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => clearDispatchWait();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A specific street is only a suggestion, not a requirement — LocationSearchBox runs
  // in advisory mode (strict={false}) so a vague pick is still accepted and only draws a
  // hint. A cleared field arrives as null.
  const handleDestinationChange = (dest) => {
    setDestination(dest);
    setError(null);
    invalidateQuote();
    if (dest) recordDestinationUse(driverId, dest);
  };

  // Quick-pick recent destinations — same UX as RiderApp's starred address chips
  // (RiderApp - 1/src/pages/NewTrip.jsx pickupChips/dropoffChips), scoped per driver.
  const destinationChips = useMemo(
    () => (driverId ? getRecentDestinations(driverId).slice(0, 3) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [driverId, isOpen]
  );

  const destinationRequired = !!selectedRate?.destinationRequired;

  const canQuote =
    !!selectedRateId &&
    !!origin &&
    (!destinationRequired || !!destination) &&
    !isQuoting;

  const handleCheckPrice = async () => {
    if (!canQuote) return;
    setError(null);
    let t = travel;
    if (destination) {
      t = await computeMileageAndDuration(origin, destination, null);
      setTravel(t);
    }
    // Freeze the exact origin used for this quote — the server signs quoteToken with a
    // fingerprint of these inputs (rateId, passengerCount, origin, destination, mileage;
    // lib/flagTripPolicy.js canonicalFingerprint/verifyQuoteToken). Submitting with a
    // different origin (e.g. a fresher GPS fix a few seconds later, even a few meters off)
    // fails verification with "Quote inputs have changed" even though nothing the driver
    // controls actually changed — so Submit must reuse this exact snapshot, not live GPS.
    setQuotedOrigin({ lat: origin.lat, lng: origin.lng });
    requestQuote({
      vehicleId,
      passengerCount: parseInt(passengerCount, 10) || 1,
      mileage: t.mileage,
      durationSeconds: t.durationSeconds,
      originLat: origin.lat,
      originLon: origin.lng,
      destinationLat: destination?.lat,
      destinationLon: destination?.lon,
    });
  };

  // Hard gate: priced AND fits AND (no pre-submit payment due, or it's settled).
  const paymentTiming = quote?.paymentTiming;
  const needsPrepay = paymentTiming === 'before_submit';
  const canSubmit =
    !!quote &&
    quote.success === true &&
    quote.allowable === true &&
    typeof quote.price === 'number' &&
    (!needsPrepay || prepay.status === 'settled') &&
    !isSubmitting &&
    !awaitingDispatch;

  const handlePassengerChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1) {
      setPassengerCount(value);
    } else if (e.target.value === '') {
      setPassengerCount('');
    }
    invalidateQuote();
  };

  const incrementPassengers = () => {
    setPassengerCount((prev) => (parseInt(prev, 10) || 0) + 1);
    invalidateQuote();
  };

  const decrementPassengers = () => {
    setPassengerCount((prev) => Math.max(1, (parseInt(prev, 10) || 1) - 1));
    invalidateQuote();
  };

  const handleClose = () => {
    clearDispatchWait();
    if (onClose) onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const count = parseInt(passengerCount, 10);
    if (!count || count < 1) {
      setError('Please enter at least 1 passenger');
      return;
    }

    // Must reuse the EXACT origin the current quote's signed quoteToken was fingerprinted
    // against (lib/flagTripPolicy.js canonicalFingerprint/verifyQuoteToken) — re-acquiring a
    // fresh GPS fix here, even a few meters/seconds different, fails server-side verification
    // with "Quote inputs have changed". canSubmit already requires a live quote, so this is
    // always set by the time Submit is reachable.
    const startCoords = quotedOrigin || origin;
    if (!startCoords) {
      setError('GPS location not available. Please wait for GPS signal.');
      return;
    }

    const now = Date.now();
    if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) {
      setError('Please wait a moment before submitting again.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    lastSubmitRef.current = now;

    try {
      const lat = startCoords.lat;
      const lng = startCoords.lng;

      // Arm the awaiting state and register the JOBMF resolver BEFORE sending FLAGM.
      // Dispatch can return JOBMF before the sendFlagTrip promise settles; if we
      // register the resolver after the await, the JOBMF arrives with a null
      // resolver and the modal hangs until the backstop timer fires.
      awaitingDispatchRef.current = true;
      setAwaitingDispatch(true);

      if (typeof registerPendingFlagAck === 'function') {
        try {
          registerPendingFlagAck((jobNumber) => {
            if (!awaitingDispatchRef.current) return;
            finishAwaitSuccess(jobNumber);
          });
        } catch (err) {
          console.warn('FlagTripModal: registerPendingFlagAck failed', err);
        }
      }

      // Listen for address-failure MSG (server can't reverse-geocode the FLAGM coords).
      comms.setFlagTripAwaitingMessageHandler((text) => {
        if (!awaitingDispatchRef.current) return;
        if (isFlagTripAddressFailureMessage(text)) {
          clearDispatchWait();
          setError(text.trim());
        }
      });

      // TRIPERR — rate/quote/payment lifecycle errors from dispatch.createFlagTrip.
      comms.setFlagTripErrorHandler((err) => {
        if (!awaitingDispatchRef.current) return;
        clearDispatchWait();
        setError(friendlyServerError(err?.message, err?.code || 'Could not create the trip.'));
      });

      dispatchWaitTimerRef.current = setTimeout(() => {
        if (!awaitingDispatchRef.current) return;
        clearDispatchWait();
        setError('Dispatch did not respond. Please try again.');
      }, DISPATCH_BACKSTOP_MS);

      const result = await comms.sendFlagTrip({
        location: { lat, lng },
        // Coords stay exactly as quoted — the server fingerprints the quoteToken over
        // origin lat/lng (lib/flagTripPolicy.js canonicalFingerprint), so these must not
        // change. The address fields are additive and outside that fingerprint.
        origin: { lat, lng },
        // Client-resolved pickup address, so dispatch can use it instead of relying on
        // its own reverse-geocode of the coords. Omitted entirely if we couldn't
        // resolve one, letting the server fall back to its existing behaviour.
        originAddress: pickup
          ? {
              address: pickup.address,
              streetNumber: pickup.streetNumber,
              streetName: pickup.streetName,
              city: pickup.city,
              state: pickup.state,
              postalCode: pickup.postalCode,
            }
          : undefined,
        destination: destination
          ? {
              latitude: destination.lat,
              longitude: destination.lon,
              address: destination.address,
              streetNumber: destination.streetNumber,
              streetName: destination.streetName,
              city: destination.city,
              state: destination.state,
              postalCode: destination.postalCode,
            }
          : undefined,
        rateId: selectedRateId,
        passengerCount: count,
        mileage: travel.mileage,
        quoteToken: quote?.quoteToken,
        prepayRef: needsPrepay ? prepay.prepayRef : undefined,
      });
      if (result && result.sent === false) {
        clearDispatchWait();
        setError('Not connected to dispatch. Check your connection and try again.');
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
    } catch (err) {
      console.error('FlagTripModal: submit error', err);
      setError('Failed to send flag trip. Please try again.');
      setIsSubmitting(false);
      clearDispatchWait();
    }
  };

  if (!isOpen) return null;

  const loc = origin;
  const busy = isSubmitting || awaitingDispatch;

  // z-[2200] — must clear the app nav bar (Header.js AppBar is zIndex 1100), the
  // BottomSheet (1500) and Messages (1600). This was z-50, so the popup rendered BEHIND
  // the blue nav bar. Matches the sibling FlagTripCreateModal/EditTripModal and the value
  // documented in index.css; still below .pac-container (10001) so the destination
  // autocomplete suggestions stay on top of this modal.
  return (
    <div className="fixed inset-0 z-[2200] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden m-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-orange-500 flex-shrink-0">
          <h2 className="text-xl font-semibold text-white">Flag Trip</h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
            disabled={isSubmitting}
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {(error || ratesError || quoteError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error || ratesError || quoteError}
            </div>
          )}

          {awaitingDispatch && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-800 text-sm flex items-center gap-2">
              <svg
                className="animate-spin h-5 w-5 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Sent to dispatch. Waiting for trip details…</span>
            </div>
          )}

          {/* Rate — resolved automatically (Flag Trip rate, or the fleet's only active
              rate); NO picker shown to the driver, per docs/DriverRequirement/
              03_create_and_flag_trip.md: "the rate is resolved by the server, never
              chosen by the driver." */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate</label>
            <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {selectedRate ? `${selectedRate.name}${selectedRate.code ? ` (${selectedRate.code})` : ''}` : 'Resolving rate…'}
            </div>
          </div>

          {/* Pickup — read-only, resolved from the driver's current GPS fix. Shown so the
              driver can see the address being recorded for this trip rather than
              discovering later that dispatch stored a placeholder. */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Pickup (current location)</label>
            <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {!origin
                ? 'Waiting for GPS…'
                : isResolvingPickup
                  ? 'Looking up address…'
                  : pickup?.address
                    ? pickup.address
                    : 'Address unavailable — GPS coordinates will be sent'}
            </div>
          </div>

          {/* Destination — required or optional per the selected rate's policy */}
          {selectedRateId && (
            <div className="mb-4">
              {!destinationRequired && !wantsDestination ? (
                <button
                  type="button"
                  onClick={() => setWantsDestination(true)}
                  disabled={busy}
                  className="text-sm font-semibold text-orange-600 hover:underline"
                >
                  + Add destination (optional)
                </button>
              ) : (
                <>
                  <LocationSearchBox
                    googleMapsLoaded={!!window.google?.maps?.places}
                    currentLocation={origin}
                    value={destination}
                    onChange={handleDestinationChange}
                    recentPlaces={destinationChips}
                    label="Destination"
                    placeholder="Search destination street address"
                    required={destinationRequired}
                    disabled={busy}
                    accent="orange"
                    strict={false}
                  />
                </>
              )}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
              Passenger count
            </label>
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={decrementPassengers}
                className="w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-2xl font-bold text-white transition-colors"
                disabled={busy || passengerCount <= 1}
              >
                -
              </button>
              <input
                type="number"
                min={1}
                value={passengerCount}
                onChange={handlePassengerChange}
                className="w-24 h-16 text-center text-3xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={busy}
              />
              <button
                type="button"
                onClick={incrementPassengers}
                className="w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-2xl font-bold text-white transition-colors"
                disabled={busy}
              >
                +
              </button>
            </div>
          </div>

          <div className="mb-2 text-center text-sm text-gray-500">
            {loc ? (
              <span className="text-green-600">GPS ready</span>
            ) : (
              <span className="text-orange-500">Waiting for GPS…</span>
            )}
          </div>

          {/* Check price */}
          <button
            type="button"
            onClick={handleCheckPrice}
            disabled={!canQuote || busy}
            className="w-full rounded-lg border border-orange-500 px-4 py-2.5 text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-50 disabled:opacity-50"
          >
            {isQuoting ? 'Checking…' : 'Check price'}
          </button>
          {!canQuote && !isQuoting && (
            <p className="mt-2 text-center text-xs text-gray-500">
              {!selectedRateId
                ? 'Resolving rate…'
                : destinationRequired && !destination
                ? 'Enter a destination to continue.'
                : !loc
                ? 'Waiting for GPS…'
                : ''}
            </p>
          )}

          {quote?.success && quote.allowable && typeof quote.price === 'number' && (
            <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{quote.priceFinal ? 'Price' : 'Estimated price'}</span>
                <span className="text-lg font-bold">${Number(quote.price).toFixed(2)}</span>
              </div>
            </div>
          )}
          {quote?.success && quote.allowable === false && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {quote.error || 'This trip does not fit before the next scheduled pickup.'}
            </div>
          )}

          {needsPrepay && quote?.success && quote.allowable && (
            <>
              <FlagTripPrepayPanel
                prepay={prepay}
                accentColor="#F97316"
                onCheckStatus={checkPrepayStatus}
                isChecking={isCheckingPrepay}
              />
              {prepay.status === 'idle' && (
                <button
                  type="button"
                  onClick={() => requestPrepay({ driverId })}
                  className="mt-3 w-full rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600"
                >
                  Collect payment
                </button>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Submitting…
              </>
            ) : awaitingDispatch ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Waiting…
              </>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlagTripModal;
